// Race plan exports. Drawn natively with jsPDF in the app's own dark theme —
// black pages, yellow headings, every section in its own bordered box.

const A = {
  bg: [10, 10, 10],
  card: [20, 20, 20],
  cardAlt: [26, 26, 26],
  line: [48, 48, 48],
  yellow: [226, 227, 10],
  text: [238, 238, 238],
  mute: [150, 150, 150],
};
const M = 34;          // page margin, pt
const PAD = 12;        // card padding
const LINE = 12.5;

export async function downloadRacePlanPdf(doc, filename = "race-plan.pdf") {
  const { default: jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const width = pageW - M * 2;
  const bottom = pageH - M - 18;

  const paint = () => {
    pdf.setFillColor(...A.bg);
    pdf.rect(0, 0, pageW, pageH, "F");
  };
  paint();

  // ---------------------------------------------------------------- header
  pdf.setFillColor(...A.yellow);
  pdf.rect(0, 0, pageW, 4, "F");
  pdf.setTextColor(...A.yellow).setFont("helvetica", "bold").setFontSize(8.5);
  pdf.text("SPC PERFORMANCE NUTRITION", M, 34);
  pdf.setTextColor(...A.text).setFontSize(21);
  const titleLines = pdf.splitTextToSize(String(doc.title || "").toUpperCase(), width).slice(0, 2);
  let y = 58;
  titleLines.forEach((l) => { pdf.text(l, M, y); y += 22; });
  pdf.setTextColor(...A.mute).setFont("helvetica", "normal").setFontSize(9);
  pdf.splitTextToSize(doc.subtitle || "", width).forEach((l) => { pdf.text(l, M, y); y += 11.5; });
  y += 10;

  // ------------------------------------------------------------ KPI strip
  const kpis = (doc.kpis || []).slice(0, 4);
  if (kpis.length) {
    const gap = 8;
    const boxW = (width - gap * (kpis.length - 1)) / kpis.length;
    const boxH = 54;
    kpis.forEach((k, i) => {
      const x = M + i * (boxW + gap);
      pdf.setFillColor(...A.card);
      pdf.setDrawColor(...A.yellow).setLineWidth(0.8);
      pdf.rect(x, y, boxW, boxH, "FD");
      pdf.setTextColor(...A.mute).setFont("helvetica", "bold").setFontSize(6.8);
      pdf.text(String(k.label).toUpperCase(), x + 8, y + 15);
      pdf.setTextColor(...A.yellow).setFontSize(17);
      pdf.text(String(k.value), x + 8, y + 36);
      if (k.sub) {
        pdf.setTextColor(...A.mute).setFont("helvetica", "normal").setFontSize(6.8);
        pdf.text(pdf.splitTextToSize(String(k.sub), boxW - 16)[0], x + 8, y + 47);
      }
    });
    y += boxH + 14;
  }

  // ------------------------------------------------------------- sections
  // Each section is turned into measured blocks so a long table can flow onto
  // the next page while every page still shows a complete bordered card.
  doc.sections.forEach((sec) => {
    const inner = width - PAD * 2;
    const blocks = [];

    const pushLines = (lines) => (lines || []).forEach((line) => {
      pdf.setFont("helvetica", "normal").setFontSize(9);
      const wrapped = pdf.splitTextToSize(String(line), inner - 14);
      blocks.push({
        h: wrapped.length * LINE + 3,
        draw: (ly) => {
          pdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...A.text);
          wrapped.forEach((l, i) => {
            if (i === 0) {
              pdf.setFillColor(...A.yellow);
              pdf.rect(M + PAD, ly + i * LINE + 3.5, 3, 3, "F");
            }
            pdf.text(l, M + PAD + 11, ly + i * LINE + 7);
          });
        },
      });
    });

    pushLines(sec.lines);

    if (sec.table) {
      const cols = sec.table.head.length;
      const colW = inner / cols;
      blocks.push({
        h: 19, keepNext: true,
        draw: (ly) => {
          pdf.setFillColor(...A.cardAlt);
          pdf.rect(M + PAD, ly, inner, 17, "F");
          pdf.setFont("helvetica", "bold").setFontSize(7.2).setTextColor(...A.yellow);
          sec.table.head.forEach((h, i) =>
            pdf.text(String(h).toUpperCase(), M + PAD + 5 + i * colW, ly + 11.5));
        },
      });
      sec.table.rows.forEach((row, ri) => {
        blocks.push({
          h: 15,
          draw: (ly) => {
            if (ri % 2 === 1) {
              pdf.setFillColor(16, 16, 16);
              pdf.rect(M + PAD, ly, inner, 15, "F");
            }
            pdf.setDrawColor(...A.line).setLineWidth(0.3);
            pdf.line(M + PAD, ly + 15, M + PAD + inner, ly + 15);
            pdf.setFont("helvetica", "normal").setFontSize(8.4).setTextColor(...A.text);
            row.forEach((cell, i) => {
              const t = pdf.splitTextToSize(String(cell), colW - 8)[0] || "";
              pdf.text(t, M + PAD + 5 + i * colW, ly + 10.5);
            });
          },
        });
      });
      blocks.push({ h: 6, draw: () => {} });
    }

    pushLines(sec.linesAfterTable);

    // Lay the blocks out, opening a fresh card whenever the page runs out.
    let i = 0;
    let first = true;
    while (i < blocks.length) {
      const headH = 26;
      if (y + headH + 30 > bottom) { pdf.addPage(); paint(); y = M; }
      const avail = bottom - y - headH - PAD;
      const chunk = [];
      let used = 0;
      while (i < blocks.length && (used + blocks[i].h <= avail || !chunk.length)) {
        used += blocks[i].h;
        chunk.push(blocks[i]);
        i += 1;
      }
      const cardH = headH + used + PAD;

      pdf.setFillColor(...A.card);
      pdf.setDrawColor(...A.line).setLineWidth(0.9);
      pdf.rect(M, y, width, cardH, "FD");
      // Heading
      pdf.setFillColor(...A.yellow);
      pdf.rect(M, y, 3.5, cardH, "F");
      pdf.setFont("helvetica", "bold").setFontSize(10).setTextColor(...A.yellow);
      const heading = (first ? sec.heading : `${sec.heading} (cont.)`).toUpperCase();
      pdf.text(pdf.splitTextToSize(heading, width - PAD * 2)[0], M + PAD, y + 17);
      pdf.setDrawColor(...A.line).setLineWidth(0.5);
      pdf.line(M + PAD, y + 22, M + width - PAD, y + 22);

      let by = y + headH;
      chunk.forEach((b) => { b.draw(by); by += b.h; });

      y += cardH + 10;
      first = false;
    }
  });

  // --------------------------------------------------------------- footer
  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p);
    pdf.setDrawColor(...A.line).setLineWidth(0.5);
    pdf.line(M, pageH - M - 10, pageW - M, pageH - M - 10);
    pdf.setFont("helvetica", "normal").setFontSize(7).setTextColor(...A.mute);
    pdf.text(doc.footer, M, pageH - M + 2);
    pdf.setTextColor(...A.yellow);
    pdf.text(`${p} / ${pages}`, pageW - M - 22, pageH - M + 2);
  }
  pdf.save(filename);
}

const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

// Print view — same dark, boxed design in a new window.
export function openRacePlanPrintView(doc) {
  const kpis = (doc.kpis || []).slice(0, 4);
  const kpiHtml = kpis.length ? `<div class="kpis">${kpis.map((k) => `
    <div class="kpi"><p class="k-label">${esc(k.label)}</p>
      <p class="k-value">${esc(k.value)}</p>
      ${k.sub ? `<p class="k-sub">${esc(k.sub)}</p>` : ""}</div>`).join("")}</div>` : "";

  const body = doc.sections.map((sec) => `
    <section>
      <h2>${esc(sec.heading)}</h2>
      ${(sec.lines || []).length ? `<ul>${sec.lines.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>` : ""}
      ${sec.table ? `<table>
        <thead><tr>${sec.table.head.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
        <tbody>${sec.table.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</tbody>
      </table>` : ""}
      ${(sec.linesAfterTable || []).length ? `<ul>${sec.linesAfterTable.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>` : ""}
    </section>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8">
    <title>${esc(doc.title)}</title>
    <style>
      *{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      body{font-family:-apple-system,BlinkMacSystemFont,"Helvetica Neue",Arial,sans-serif;
           color:#eee;background:#0a0a0a;margin:0;padding:28px;max-width:860px}
      header{border-top:4px solid #E2E30A;padding-top:14px;margin-bottom:18px}
      .brand{font-size:9px;letter-spacing:.24em;font-weight:700;color:#E2E30A;text-transform:uppercase;margin:0}
      h1{font-size:28px;margin:8px 0 6px;text-transform:uppercase;letter-spacing:-.6px;color:#fff}
      .sub{font-size:11.5px;color:#9a9a9a;margin:0}
      .kpis{display:flex;gap:8px;margin:0 0 16px}
      .kpi{flex:1;border:1px solid #E2E30A;background:#141414;padding:10px 12px}
      .k-label{font-size:8px;letter-spacing:.16em;text-transform:uppercase;color:#9a9a9a;font-weight:700;margin:0}
      .k-value{font-size:22px;color:#E2E30A;font-weight:800;margin:6px 0 0}
      .k-sub{font-size:8.5px;color:#9a9a9a;margin:3px 0 0}
      section{page-break-inside:avoid;background:#141414;border:1px solid #303030;
              border-left:4px solid #E2E30A;padding:14px 16px;margin-bottom:12px}
      h2{font-size:11px;text-transform:uppercase;letter-spacing:.16em;margin:0 0 10px;
         color:#E2E30A;padding-bottom:8px;border-bottom:1px solid #303030}
      ul{margin:0;padding-left:16px;list-style:none}
      li{font-size:11.5px;line-height:1.55;margin-bottom:5px;position:relative;padding-left:12px;color:#e5e5e5}
      li:before{content:"";position:absolute;left:0;top:7px;width:4px;height:4px;background:#E2E30A}
      table{width:100%;border-collapse:collapse;margin-top:10px;font-size:11px}
      th{text-align:left;background:#1c1c1c;padding:7px;text-transform:uppercase;
         font-size:8.5px;letter-spacing:.1em;color:#E2E30A;border-bottom:1px solid #303030}
      td{padding:7px;border-bottom:1px solid #242424;color:#e5e5e5}
      tr:nth-child(even) td{background:#101010}
      footer{margin-top:22px;border-top:1px solid #303030;padding-top:10px;font-size:9px;color:#777}
      @media print{body{padding:0;max-width:none}@page{margin:12mm}}
    </style></head><body>
    <header>
      <p class="brand">SPC Performance Nutrition</p>
      <h1>${esc(doc.title)}</h1>
      <p class="sub">${esc(doc.subtitle)}</p>
    </header>
    ${kpiHtml}
    ${body}
    <footer>${esc(doc.footer)}</footer>
    <script>window.onload=function(){setTimeout(function(){window.print()},250)}</script>
    </body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}
